import { useState } from 'react'
import { Box, CircularProgress, Dialog, DialogContent, TextField, Typography } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { saveUserTemplate } from '../../../../../apiLayer/processHandlers/userTemplates'
import { QUERY_KEYS } from '../../../../../apiLayer/queryKeys'
import { Button } from '../../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import type { Activity } from '../../types'

const MAX_NAME_LENGTH = 40

const textFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: INTERAX_COLORS.heritageBlue40,
    color: INTERAX_COLORS.white,
    borderRadius: '8px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: INTERAX_COLORS.thunderGrey
  },
  '& .MuiInputBase-input::placeholder': {
    color: INTERAX_COLORS.thunderGrey,
    opacity: 1
  }
}

interface Props {
  open: boolean
  activities: Activity[]
  onClose: () => void
}

export function SaveActivityTemplateDialog({ open, activities, onClose }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)

  const trimmed = name.trim()
  const isValid = trimmed.length > 0 && trimmed.length <= MAX_NAME_LENGTH && activities.length > 0

  async function handleSave() {
    if (!isValid) return
    setLoading(true)
    try {
      await saveUserTemplate(trimmed, activities)
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] })
      setName('')
      onClose()
    } catch {
      toast.error(t('common.errorGeneric'))
    } finally {
      setLoading(false)
    }
  }

  function handleClose() {
    setName('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: INTERAX_COLORS.heritageBlue,
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)'
          }
        }
      }}
    >
      <DialogContent sx={{ p: 4, position: 'relative' }}>
        <Box
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: '50%',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            '&:hover': { backgroundColor: INTERAX_COLORS.thunderGrey20 }
          }}
        >
          <XMarkIcon style={{ width: '20px', height: '20px', color: INTERAX_COLORS.white }} />
        </Box>

        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: INTERAX_COLORS.white, mb: 1 }}>
          {t('facilitator.sprintCreation.step2.templates.saveDialog.title')}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey, mb: 3 }}>
          {t('facilitator.sprintCreation.step2.templates.saveDialog.subtitle')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: INTERAX_COLORS.white }}>
            {t('facilitator.sprintCreation.step2.templates.saveDialog.label')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.thunderGrey }}>
            {name.length} / {MAX_NAME_LENGTH}
          </Typography>
        </Box>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, MAX_NAME_LENGTH))}
          placeholder={t('facilitator.sprintCreation.step2.templates.saveDialog.placeholder')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave()
          }}
          inputProps={{ maxLength: MAX_NAME_LENGTH }}
          sx={textFieldSx}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="secondary" onClick={handleClose} disabled={loading} fullWidth>
            {t('facilitator.sprintCreation.step2.templates.saveDialog.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!isValid || loading}
            fullWidth
            icon={loading ? <CircularProgress size={14} sx={{ color: INTERAX_COLORS.white }} /> : undefined}
          >
            {t('facilitator.sprintCreation.step2.templates.saveDialog.save')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
